import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useCustomer, useCustomerList } from '../customers/hooks';
import { useRecordConfig } from '../customers/recordHooks';
import {
  downloadCustomerHistoryPdf,
  emailCustomerHistory,
  useCustomerReport,
  useExpiringDocumentsReport,
  useOutstandingReport,
  useRevenueReport,
  useVisitsReport
} from './hooks';
import { kindLabel } from '../receipts/kindLabel';
import { useReceiptList } from '../receipts/hooks';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import type { Granularity } from './types';
import type { ReceiptKind } from '../receipts/types';

type Tab = 'revenue' | 'outstanding' | 'customer' | 'visits' | 'expiring';

const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'customer', label: 'By customer' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'outstanding', label: 'Outstanding' },
  { id: 'visits', label: 'Visits' },
  { id: 'expiring', label: 'Expiring documents' }
];

export function ReportsPage() {
  usePageTitle('Reports');
  const [searchParams] = useSearchParams();
  // Deep-link support (e.g. the customer page's Report button):
  // /reports?tab=customer&customerId=<id>. Falls back to the customer tab.
  const tabParam = searchParams.get('tab');
  const initialTab: Tab = TABS.some((t) => t.id === tabParam) ? (tabParam as Tab) : 'customer';
  const [tab, setTab] = useState<Tab>(initialTab);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Reports</h1>
      </div>
      <nav className="flex gap-2 border-b border-slate-200 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-sm whitespace-nowrap border-b-2 -mb-px ${
              tab === t.id
                ? 'border-sky-500 text-sky-700 font-medium'
                : 'border-transparent text-slate-600 hover:text-sky-600'
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      {tab === 'revenue' && <RevenueTab />}
      {tab === 'outstanding' && <OutstandingTab />}
      {tab === 'customer' && <CustomerTab />}
      {tab === 'visits' && <VisitsTab />}
      {tab === 'expiring' && <ExpiringTab />}
    </div>
  );
}

type Preset = 'thisWeek' | 'thisMonth' | 'last30' | 'ytd' | 'lastYear' | 'last2Years' | 'custom';

const PRESETS: Array<{ id: Preset; label: string }> = [
  { id: 'thisWeek',   label: 'This week' },
  { id: 'thisMonth',  label: 'This month' },
  { id: 'last30',     label: 'Last 30 days' },
  { id: 'ytd',        label: 'YTD' },
  { id: 'lastYear',   label: 'Last year' },
  { id: 'last2Years', label: 'Last 2 years' },
  { id: 'custom',     label: 'Custom…' }
];

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function endOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
}

function rangeFor(
  preset: Exclude<Preset, 'custom'>,
  today = new Date()
): { from: string; to: string; granularity: Granularity } {
  const iso = (d: Date) => d.toISOString();
  switch (preset) {
    case 'thisWeek': {
      const d = new Date(today);
      const dow = d.getDay() === 0 ? 6 : d.getDay() - 1; // Monday=0
      d.setDate(d.getDate() - dow);
      return { from: iso(startOfDay(d)), to: iso(endOfDay(new Date())), granularity: 'day' };
    }
    case 'thisMonth': {
      const from = new Date(today.getFullYear(), today.getMonth(), 1);
      return { from: iso(from), to: iso(endOfDay(new Date())), granularity: 'week' };
    }
    case 'last30': {
      const from = startOfDay(new Date(today.getTime() - 30 * 86_400_000));
      return { from: iso(from), to: iso(endOfDay(new Date())), granularity: 'week' };
    }
    case 'ytd': {
      const from = new Date(today.getFullYear(), 0, 1);
      return { from: iso(from), to: iso(endOfDay(new Date())), granularity: 'month' };
    }
    case 'lastYear': {
      const from = new Date(today.getFullYear() - 1, 0, 1);
      const to = new Date(today.getFullYear() - 1, 11, 31, 23, 59, 59);
      return { from: iso(from), to: iso(to), granularity: 'month' };
    }
    case 'last2Years': {
      const from = new Date(today.getFullYear() - 2, today.getMonth(), today.getDate());
      return { from: iso(startOfDay(from)), to: iso(endOfDay(new Date())), granularity: 'month' };
    }
  }
}

/**
 * Resolve a preset + custom-range selection into the `from`/`to` ISO strings
 * the report hooks expect, plus the preset's default chart granularity. Shared
 * by the Revenue and Visits tabs so their range pickers behave identically.
 */
function computeRange(
  preset: Preset,
  customFrom: string,
  customTo: string
): { from?: string; to?: string; customInvalid: boolean; presetGranularity: Granularity } {
  if (preset === 'custom') {
    if (customFrom && customTo) {
      const f = new Date(customFrom);
      const t = new Date(customTo);
      if (!Number.isNaN(f.getTime()) && !Number.isNaN(t.getTime()) && f.getTime() <= t.getTime()) {
        return {
          from: startOfDay(f).toISOString(),
          to: endOfDay(t).toISOString(),
          customInvalid: false,
          presetGranularity: 'month'
        };
      }
    }
    return { from: undefined, to: undefined, customInvalid: true, presetGranularity: 'month' };
  }
  const r = rangeFor(preset);
  return { from: r.from, to: r.to, customInvalid: false, presetGranularity: r.granularity };
}

/**
 * Preset pills + Custom from/to date inputs, shared by the Revenue and Visits
 * tabs. Side effects on preset change (clearing drill-down, resetting
 * granularity) are the caller's job via `onPickPreset`.
 */
function RangePicker({
  preset,
  onPickPreset,
  customFrom,
  customTo,
  onCustomFrom,
  onCustomTo,
  customInvalid
}: {
  preset: Preset;
  onPickPreset: (id: Preset) => void;
  customFrom: string;
  customTo: string;
  onCustomFrom: (v: string) => void;
  onCustomTo: (v: string) => void;
  customInvalid: boolean;
}) {
  return (
    <>
      <div className="flex gap-2 flex-wrap">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => onPickPreset(p.id)}
            className={`px-3 py-1 rounded-full text-sm border transition-colors ${
              preset === p.id
                ? 'bg-sky-100 border-sky-300 text-sky-800 font-medium'
                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {preset === 'custom' && (
        <div className="flex gap-3 items-end flex-wrap">
          <label className="text-sm text-slate-600 flex flex-col gap-1">
            From
            <input
              type="date"
              value={customFrom}
              onChange={(e) => onCustomFrom(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1"
            />
          </label>
          <label className="text-sm text-slate-600 flex flex-col gap-1">
            To
            <input
              type="date"
              value={customTo}
              min={customFrom || undefined}
              onChange={(e) => onCustomTo(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1"
            />
          </label>
          {customInvalid && customFrom && customTo && (
            <span className="text-sm text-red-700">From must be on or before To.</span>
          )}
        </div>
      )}
    </>
  );
}

function getISOWeek(d: Date): number {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = tmp.getUTCDay() === 0 ? 7 : tmp.getUTCDay();
  tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  return Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
}

function formatBucket(bucket: string, granularity: Granularity): string {
  const d = new Date(bucket);
  if (Number.isNaN(d.getTime())) return bucket;
  if (granularity === 'day') {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  if (granularity === 'week') {
    return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} (W${getISOWeek(d)})`;
  }
  return d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

// Mirrors the server-side cap on GET /api/v1/receipts (.Take(200)). If a Paid
// fetch comes back at this length we can't be sure it covers the whole range,
// so the revenue drill-down hides itself rather than show partial data.
const RECEIPTS_LIST_CAP = 200;

function RevenueTab() {
  const [preset, setPreset] = useState<Preset>('ytd');
  const [granularity, setGranularity] = useLocalStorage<Granularity>('reports.granularity', 'month');
  const [granularityTouched, setGranularityTouched] = useState(false);
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [selectedBucket, setSelectedBucket] = useState<string | null>(null);

  const { from, to, customInvalid, presetGranularity } = computeRange(preset, customFrom, customTo);
  // Preset default only when not custom and the user hasn't explicitly picked a
  // granularity since landing on the tab; otherwise honor the stored choice.
  const granularityEffective: Granularity =
    preset !== 'custom' && !granularityTouched ? presetGranularity : granularity;

  const { data, isLoading } = useRevenueReport(
    granularityEffective,
    customInvalid ? undefined : from,
    customInvalid ? undefined : to
  );

  // Drill-down source: all Paid receipts, filtered client-side to the clicked
  // bucket window. The list endpoint caps at RECEIPTS_LIST_CAP rows.
  const { data: paidReceipts } = useReceiptList({ status: 'Paid' });
  const receiptsCapped = (paidReceipts?.length ?? 0) >= RECEIPTS_LIST_CAP;

  const buckets = data?.buckets ?? [];
  const drillItems = useMemo(() => {
    if (selectedBucket === null || receiptsCapped || !paidReceipts) return [];
    const idx = buckets.findIndex((b) => b.startUtc === selectedBucket);
    if (idx === -1) return [];
    const start = new Date(selectedBucket).getTime();
    const isLast = idx === buckets.length - 1;
    // Window is [bucketStart, nextBucketStart); the final bucket runs through
    // the report's `to` so its total reconciles with the bar.
    const endExclusive = isLast ? Number.POSITIVE_INFINITY : new Date(buckets[idx + 1].startUtc).getTime();
    const toInclusive = isLast ? (to ? new Date(to).getTime() : Number.POSITIVE_INFINITY) : Number.POSITIVE_INFINITY;
    return paidReceipts
      .filter((r) => {
        if (!r.paidAtUtc) return false;
        const t = new Date(r.paidAtUtc).getTime();
        return t >= start && (isLast ? t <= toInclusive : t < endExclusive);
      })
      .sort((a, b) => new Date(a.paidAtUtc!).getTime() - new Date(b.paidAtUtc!).getTime());
  }, [selectedBucket, receiptsCapped, paidReceipts, buckets, to]);

  const onPickGranularity = (g: Granularity) => {
    setGranularity(g);
    setGranularityTouched(true);
    setSelectedBucket(null);
  };

  const onBarClick = (payload: { startUtc?: string } | undefined) => {
    const clicked = payload?.startUtc;
    if (!clicked) return;
    setSelectedBucket((cur) => (cur === clicked ? null : clicked));
  };

  const onPickPreset = (id: Preset) => {
    setPreset(id);
    setGranularityTouched(false);
    setSelectedBucket(null);
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-800">Paid revenue</h2>
        <div className="flex gap-1 text-sm">
          {(['day', 'week', 'month'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => onPickGranularity(g)}
              className={`px-3 py-1 rounded border ${
                granularityEffective === g
                  ? 'bg-sky-50 border-sky-300 text-sky-800'
                  : 'bg-white border-slate-200 text-slate-600'
              }`}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <RangePicker
        preset={preset}
        onPickPreset={onPickPreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
        customInvalid={customInvalid}
      />

      {customInvalid ? (
        <p className="text-sm text-slate-500 italic">Pick a valid date range to see results.</p>
      ) : isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !data || data.buckets.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No paid receipts in the selected range.</p>
      ) : (
        <>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="startUtc"
                  tickFormatter={(v: string) => formatBucket(v, granularityEffective)}
                  stroke="#64748b"
                  fontSize={12}
                />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  labelFormatter={(v) => formatBucket(String(v), granularityEffective)}
                  formatter={(v) => (typeof v === 'number' ? v.toFixed(2) : String(v))}
                  contentStyle={{ fontSize: 12 }}
                />
                <Bar
                  dataKey="total"
                  fill="#0ea5e9"
                  cursor="pointer"
                  onClick={(d: { startUtc?: string; payload?: { startUtc?: string } }) =>
                    onBarClick(d?.payload ?? d)
                  }
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <p className="text-sm text-slate-700 text-right">
            Grand total: <strong>{data.grandTotal.toFixed(2)}</strong>
          </p>

          {selectedBucket !== null && (
            receiptsCapped ? (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
                Too many paid receipts to break down reliably (over {RECEIPTS_LIST_CAP}). Open a
                customer's history for a complete per-receipt view.
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-100">
                  <h3 className="text-sm font-medium text-slate-700">
                    Receipts for {formatBucket(selectedBucket, granularityEffective)}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setSelectedBucket(null)}
                    className="text-slate-400 hover:text-slate-700 text-sm"
                    aria-label="Close breakdown"
                  >
                    ✕
                  </button>
                </div>
                {drillItems.length === 0 ? (
                  <p className="text-sm text-slate-500 italic px-3 py-3">
                    No paid receipts in this bucket.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-600">
                      <tr>
                        <th className="text-left px-3 py-2 font-medium">Number</th>
                        <th className="text-left px-3 py-2 font-medium">Client</th>
                        <th className="text-left px-3 py-2 font-medium">Type</th>
                        <th className="text-right px-3 py-2 font-medium">Amount</th>
                        <th className="text-left px-3 py-2 font-medium">Paid</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drillItems.map((r) => (
                        <tr key={r.id} className="border-t border-slate-100">
                          <td className="px-3 py-2 font-mono text-xs">
                            <Link to={`/receipts/${r.id}`} className="text-sky-600 hover:text-sky-700">
                              {r.receiptNumber}
                            </Link>
                          </td>
                          <td className="px-3 py-2 text-slate-700">{r.customerName}</td>
                          <td className="px-3 py-2 text-slate-600">{kindLabel(r.kind)}</td>
                          <td className="px-3 py-2 text-right font-mono">
                            {r.total.toFixed(2)} {r.currency}
                          </td>
                          <td className="px-3 py-2 text-slate-500 text-xs">
                            {r.paidAtUtc ? new Date(r.paidAtUtc).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )
          )}
        </>
      )}
    </section>
  );
}

function OutstandingTab() {
  const [days, setDays] = useState(7);
  const { data, isLoading } = useOutstandingReport(days);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-800">Outstanding receipts</h2>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Older than
          <input
            type="number"
            min={0}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-slate-300 rounded px-2 py-1 w-20 text-right"
          />
          days
        </label>
      </div>
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !data || data.rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">Nothing outstanding over {days} days. 🎉</p>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            Total outstanding: <strong>{data.totalOutstanding.toFixed(2)}</strong> across{' '}
            {data.rows.length} receipt{data.rows.length === 1 ? '' : 's'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Number</th>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-right px-3 py-2 font-medium">Amount</th>
                  <th className="text-right px-3 py-2 font-medium">Age</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.receiptId} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono text-xs">
                      <Link to={`/receipts/${r.receiptId}`} className="text-sky-600 hover:text-sky-700">
                        {r.receiptNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-2">{r.customerName}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.total.toFixed(2)} {r.currency}</td>
                    <td className="px-3 py-2 text-right text-slate-500">{r.ageDays}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function CustomerTab() {
  const [searchParams] = useSearchParams();
  // Preselect from the deep link (?customerId=) — e.g. the customer page's Report button.
  const [customerId, setCustomerId] = useState(searchParams.get('customerId') ?? '');
  const [filter, setFilter] = useState('');
  // Date pills mirror the Visits tab; default to the widest preset so the
  // history reads as "everything" until the user narrows it.
  const [preset, setPreset] = useState<Preset>('last2Years');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [busy, setBusy] = useState<null | 'download' | 'email'>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const { from, to, customInvalid } = computeRange(preset, customFrom, customTo);
  const { data: customers } = useCustomerList({
    search: filter, includeInactive: false, page: 1, pageSize: 25
  });
  // A deep-linked / chosen customer may fall outside the 25-row search list, so
  // resolve it directly and make sure it's a selectable option (and visible name).
  const { data: selectedCustomer } = useCustomer(customerId || undefined);
  const customerOptions = useMemo(() => {
    const list = (customers?.items ?? []).map((c) => ({ id: c.id, displayName: c.displayName }));
    if (selectedCustomer && !list.some((c) => c.id === selectedCustomer.id)) {
      return [{ id: selectedCustomer.id, displayName: selectedCustomer.displayName }, ...list];
    }
    return list;
  }, [customers, selectedCustomer]);
  const { data, isLoading } = useCustomerReport(
    customerId || undefined,
    customInvalid ? undefined : from,
    customInvalid ? undefined : to
  );

  const actionsDisabled = !customerId || customInvalid || busy !== null;

  const onDownload = async () => {
    if (!customerId) return;
    setBusy('download');
    setActionMsg(null);
    try {
      await downloadCustomerHistoryPdf(customerId, from, to);
    } catch {
      setActionMsg('Could not download the PDF. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const onEmail = async () => {
    if (!customerId) return;
    setBusy('email');
    setActionMsg(null);
    try {
      await emailCustomerHistory(customerId, from, to);
      setActionMsg('Report emailed to you.');
    } catch {
      setActionMsg('Could not email the report. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Client history</h2>
      <div className="grid sm:grid-cols-[1fr,1fr] gap-3">
        <input
          type="search"
          placeholder="Search clients…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 min-h-[44px]"
        />
        <select
          value={customerId}
          onChange={(e) => setCustomerId(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2 min-h-[44px]"
        >
          <option value="">Pick a customer…</option>
          {customerOptions.map((c) => (
            <option key={c.id} value={c.id}>{c.displayName}</option>
          ))}
        </select>
      </div>

      <RangePicker
        preset={preset}
        onPickPreset={setPreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
        customInvalid={customInvalid}
      />

      <div className="flex items-center gap-3 flex-wrap">
        <button
          type="button"
          onClick={onDownload}
          disabled={actionsDisabled}
          className="px-3 py-2 rounded border border-slate-300 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy === 'download' ? 'Preparing…' : 'Download PDF'}
        </button>
        <button
          type="button"
          onClick={onEmail}
          disabled={actionsDisabled}
          className="px-3 py-2 rounded border border-sky-300 bg-sky-50 text-sm text-sky-800 hover:bg-sky-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy === 'email' ? 'Sending…' : 'Email report'}
        </button>
        {actionMsg && <span className="text-sm text-slate-600">{actionMsg}</span>}
      </div>

      {!customerId ? (
        <p className="text-sm text-slate-500 italic">Pick a customer to see their history.</p>
      ) : customInvalid ? (
        <p className="text-sm text-slate-500 italic">Pick a valid date range to see results.</p>
      ) : isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !data ? (
        <p className="text-sm text-slate-500 italic">Not found.</p>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Paid" value={data.totalPaid.toFixed(2)} />
            <Stat label="Outstanding" value={data.totalOutstanding.toFixed(2)} />
            <Stat label="Visits" value={String(data.visitCount)} />
            <Stat
              label="Last visit"
              value={data.lastVisitStartUtc ? new Date(data.lastVisitStartUtc).toLocaleDateString() : '—'}
            />
          </div>

          {data.receipts.length > 0 && (
            <details open className="border border-slate-200 rounded">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
                Receipts ({data.receipts.length})
              </summary>
              <table className="w-full text-sm">
                <tbody>
                  {data.receipts.map((r) => (
                    <tr key={r.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-mono text-xs">
                        <Link to={`/receipts/${r.id}`} className="text-sky-600 hover:text-sky-700">{r.receiptNumber}</Link>
                      </td>
                      <td className="px-3 py-2">{kindLabel(r.kind as ReceiptKind)}</td>
                      <td className="px-3 py-2">{r.status}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{new Date(r.issuedAtUtc).toLocaleDateString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{r.total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {data.visits.length > 0 && (
            <details open className="border border-slate-200 rounded">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
                Visits ({data.visits.length})
              </summary>
              <table className="w-full text-sm">
                <tbody>
                  {data.visits.map((v) => (
                    <tr key={v.visitId} className="border-t border-slate-100">
                      <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                        {visitDateLabel(v.startUtc, v.endUtc)}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{v.status}</td>
                      <td className="px-3 py-2 text-slate-500">{v.locationAddress || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}

          {data.activeDocuments.length > 0 && (
            <details open className="border border-slate-200 rounded">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-slate-700">
                Active documents ({data.activeDocuments.length})
              </summary>
              <table className="w-full text-sm">
                <tbody>
                  {data.activeDocuments.map((d) => (
                    <tr key={d.id} className="border-t border-slate-100">
                      <td className="px-3 py-2">{d.templateName}</td>
                      <td className="px-3 py-2 text-slate-600">{d.status}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {d.effectiveFromUtc
                          ? `${new Date(d.effectiveFromUtc).toLocaleDateString()} – ${new Date(d.effectiveUntilUtc).toLocaleDateString()}`
                          : `Valid until ${new Date(d.effectiveUntilUtc).toLocaleDateString()}`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </section>
  );
}

function VisitsTab() {
  // Defaults to the Last 2 years preset; the selected range drives from/to.
  const [preset, setPreset] = useState<Preset>('last2Years');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const { data: recordConfig } = useRecordConfig();
  const recordsLabel = recordConfig?.pluralLabel ?? 'Records';

  const { from, to, customInvalid } = computeRange(preset, customFrom, customTo);
  const { data, isLoading } = useVisitsReport(
    customInvalid ? undefined : from,
    customInvalid ? undefined : to
  );

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <h2 className="text-lg font-semibold text-slate-800">Visits</h2>

      <RangePicker
        preset={preset}
        onPickPreset={setPreset}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFrom={setCustomFrom}
        onCustomTo={setCustomTo}
        customInvalid={customInvalid}
      />

      {customInvalid ? (
        <p className="text-sm text-slate-500 italic">Pick a valid date range to see results.</p>
      ) : isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !data || data.rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No visits in the selected range.</p>
      ) : (
        <>
          <p className="text-sm text-slate-700">
            {data.rows.length} visit{data.rows.length === 1 ? '' : 's'}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Date</th>
                  <th className="text-left px-3 py-2 font-medium">Client</th>
                  <th className="text-left px-3 py-2 font-medium">{recordsLabel}</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-left px-3 py-2 font-medium">Service address</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.visitId} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-500 text-xs whitespace-nowrap">
                      {new Date(r.startUtc).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{r.customerName}</td>
                    <td className="px-3 py-2 text-slate-600">{r.recordNames || '—'}</td>
                    <td className="px-3 py-2 text-slate-600">{r.status}</td>
                    <td className="px-3 py-2 text-slate-500">{r.locationAddress || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}

function ExpiringTab() {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useExpiringDocumentsReport(days);

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-6 space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-800">Expiring documents</h2>
        <label className="text-sm text-slate-600 flex items-center gap-2">
          Within
          <input
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="border border-slate-300 rounded px-2 py-1 w-20 text-right"
          />
          days
        </label>
      </div>
      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : !data || data.rows.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No documents expiring in the next {days} days.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Template</th>
                <th className="text-left px-3 py-2 font-medium">Client</th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-right px-3 py-2 font-medium">Days left</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.documentInstanceId} className="border-t border-slate-100">
                  <td className="px-3 py-2">{r.templateName}</td>
                  <td className="px-3 py-2">
                    <Link to={`/customers/${r.customerId}`} className="text-sky-600 hover:text-sky-700">
                      {r.customerName}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-slate-600">{r.status}</td>
                  <td className={`px-3 py-2 text-right font-mono ${r.daysUntilExpiry <= 7 ? 'text-red-700' : 'text-slate-700'}`}>
                    {r.daysUntilExpiry}d
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Single date when a visit starts and ends the same day, otherwise a range.
function visitDateLabel(startUtc: string, endUtc: string): string {
  const start = new Date(startUtc);
  const end = new Date(endUtc);
  const startStr = start.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  if (start.toDateString() === end.toDateString()) return startStr;
  return `${startStr} – ${end.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}`;
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-slate-200 rounded p-3 bg-slate-50">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-800">{value}</div>
    </div>
  );
}
